import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
  makeStyles
} from "@material-ui/core";
import {
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  DeleteOutline as DeleteOutlineIcon,
  ExitToApp as ExitToAppIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  FiberManualRecord as FiberManualRecordIcon
} from "@material-ui/icons";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import toastUtils from "../../utils/toastUtils";
import ButtonWithSpinner from "../ButtonWithSpinner";

const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(2),
    minWidth: 460,
    [theme.breakpoints.down("sm")]: {
      minWidth: "100%",
    },
  },
  headerBox: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.action.hover,
    borderRadius: theme.spacing(1),
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.95rem",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    color: theme.palette.text.primary,
  },
  participantItem: {
    borderRadius: theme.spacing(1),
    marginBottom: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
  },
  ownerItem: {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.mode === "dark" ? "rgba(25, 118, 210, 0.08)" : "rgba(25, 118, 210, 0.04)",
  },
  statusChip: {
    fontSize: "0.75rem",
    height: 22,
  },
  onlineIcon: {
    fontSize: 10,
    color: "#44b700",
    marginRight: 4,
  },
  offlineIcon: {
    fontSize: 10,
    color: "#d32f2f",
    marginRight: 4,
  },
  sameQueueBadge: {
    fontSize: "0.7rem",
    backgroundColor: theme.palette.success.main,
    color: "#fff",
    height: 20,
    marginLeft: theme.spacing(0.5),
  },
}));

const filterOptions = createFilterOptions({
  trim: true,
});

const TicketParticipantsModal = ({ open, onClose, ticket, onParticipantChange }) => {
  const classes = useStyles();
  const { user: currentUser } = useContext(AuthContext);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [searchParam, setSearchParam] = useState("");
  const [searchingUsers, setSearchingUsers] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Carregar participantes do ticket
  const loadParticipants = async () => {
    if (!ticket?.id) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/tickets/${ticket.id}/participants`);
      if (isMounted.current) {
        setParticipants(data || []);
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (open && ticket?.id) {
      loadParticipants();
      setSelectedUser(null);
      setSearchParam("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, ticket?.id]);

  // Carregar usuários para convite
  useEffect(() => {
    if (!open) return;

    const fetchUsers = async () => {
      setSearchingUsers(true);
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam },
        });
        if (isMounted.current) {
          // Filtrar usuários que já são dono do ticket ou já são participantes
          const existingUserIds = new Set([
            ticket.userId,
            ...participants.map((p) => p.userId),
          ]);
          const filtered = (data.users || []).filter((u) => !existingUserIds.has(u.id));
          setAvailableUsers(filtered);
        }
      } catch (err) {
        toastError(err);
      } finally {
        if (isMounted.current) setSearchingUsers(false);
      }
    };

    const delayDebounceFn = setTimeout(() => {
      fetchUsers();
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, open, participants, ticket.userId]);

  // Enviar convite
  const handleInvite = async () => {
    if (!selectedUser) return;
    setInviting(true);
    try {
      await api.post(`/tickets/${ticket.id}/participants/invite`, {
        userId: selectedUser.id,
      });
      toastUtils.success("Convite enviado com sucesso!");
      setSelectedUser(null);
      await loadParticipants();
      if (typeof onParticipantChange === "function") {
        onParticipantChange();
      }
    } catch (err) {
      toastError(err);
    } finally {
      if (isMounted.current) setInviting(false);
    }
  };

  // Remover participante ou cancelar convite
  const handleRemove = async (participantUserId) => {
    try {
      await api.delete(`/tickets/${ticket.id}/participants/${participantUserId}`);
      toastUtils.success("Participante removido do atendimento.");
      await loadParticipants();
      if (typeof onParticipantChange === "function") {
        onParticipantChange();
      }
    } catch (err) {
      toastError(err);
    }
  };

  // Sair do atendimento (quando o usuário logado for co-atendente)
  const handleLeave = async () => {
    try {
      await api.post(`/tickets/${ticket.id}/participants/leave`);
      toastUtils.success("Você saiu do atendimento.");
      onClose();
      if (typeof onParticipantChange === "function") {
        onParticipantChange();
      }
    } catch (err) {
      toastError(err);
    }
  };

  const isTicketOwner = !ticket?.userId || ticket?.userId === currentUser?.id;
  const isAdmin = currentUser?.profile === "admin";
  const canInvite = isTicketOwner || isAdmin;
  const isParticipant = participants.some(
    (p) => p.userId === currentUser?.id && p.status === "accepted"
  );

  // Verificar se um usuário possui a mesma fila do ticket
  const hasSameQueue = (user) => {
    if (!ticket?.queueId || !user?.queues) return false;
    return user.queues.some((q) => q.id === ticket.queueId);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <GroupIcon color="primary" />
          <Typography variant="h6" style={{ fontWeight: 700 }}>
            Participantes do Atendimento
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers className={classes.dialogContent}>
        {/* Informações do Ticket */}
        <Box className={classes.headerBox}>
          <Avatar src={ticket?.contact?.profilePicUrl} style={{ width: 44, height: 44 }}>
            {ticket?.contact?.name?.charAt(0) || "T"}
          </Avatar>
          <Box flex={1}>
            <Typography variant="subtitle2" style={{ fontWeight: 700 }}>
              {ticket?.contact?.name || "Contato"}
            </Typography>
            <Box display="flex" alignItems="center" gap={1} mt={0.5}>
              <Typography variant="caption" color="textSecondary">
                Ticket #{ticket?.id}
              </Typography>
              {ticket?.queue && (
                <Chip
                  label={`Fila: ${ticket.queue.name}`}
                  size="small"
                  style={{
                    backgroundColor: ticket.queue.color || "#1976d2",
                    color: "#fff",
                    height: 20,
                    fontSize: "0.7rem",
                  }}
                />
              )}
            </Box>
          </Box>
        </Box>

        {/* Formulário de Convidar Atendente (Apenas Titular ou Admin) */}
        {canInvite && (
          <>
            <Typography className={classes.sectionTitle}>
              Convidar Atendente para este Ticket
            </Typography>
            <Box display="flex" gap={1} alignItems="flex-start" mb={2}>
              <Autocomplete
                fullWidth
                size="small"
                options={availableUsers}
                filterOptions={filterOptions}
                getOptionLabel={(option) => `${option.name}`}
                value={selectedUser}
                onChange={(e, newValue) => setSelectedUser(newValue)}
                loading={searchingUsers}
                noOptionsText="Nenhum atendente disponível"
                renderOption={(option) => (
                  <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                    <Box display="flex" alignItems="center" gap={1}>
                      <FiberManualRecordIcon
                        className={option.online ? classes.onlineIcon : classes.offlineIcon}
                      />
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                    {hasSameQueue(option) && (
                      <Chip
                        label="Mesma Fila"
                        size="small"
                        className={classes.sameQueueBadge}
                      />
                    )}
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Selecione um atendente..."
                    variant="outlined"
                    onChange={(e) => setSearchParam(e.target.value)}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {searchingUsers ? <CircularProgress color="inherit" size={18} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />

              <ButtonWithSpinner
                variant="contained"
                color="primary"
                disabled={!selectedUser || inviting}
                loading={inviting}
                onClick={handleInvite}
                startIcon={<PersonAddIcon />}
                style={{ height: 40, whiteSpace: "nowrap" }}
              >
                Convidar
              </ButtonWithSpinner>
            </Box>

            <Divider style={{ margin: "16px 0" }} />
          </>
        )}

        {/* Lista de Participantes Atuais */}
        <Typography className={classes.sectionTitle}>
          Equipe no Atendimento
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List disablePadding>
            {/* Responsável Principal */}
            {ticket.user && (
              <ListItem className={`${classes.participantItem} ${classes.ownerItem}`}>
                <ListItemAvatar>
                  <Avatar style={{ backgroundColor: "#1976d2", color: "#fff" }}>
                    {ticket.user.name?.charAt(0)}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography variant="body2" style={{ fontWeight: 600 }}>
                        {ticket.user.name}
                      </Typography>
                      <Chip
                        label="Responsável Principal"
                        size="small"
                        color="primary"
                        className={classes.statusChip}
                      />
                    </Box>
                  }
                  secondary="Atendente titular do ticket"
                />
              </ListItem>
            )}

            {/* Co-atendentes e Convites Pendentes */}
            {participants.length === 0 ? (
              <Typography variant="body2" color="textSecondary" style={{ fontStyle: "italic", padding: "8px 0" }}>
                Nenhum co-atendente adicionado a este ticket ainda.
              </Typography>
            ) : (
              participants.map((participant) => {
                const isAccepted = participant.status === "accepted";
                const isPending = participant.status === "pending";

                return (
                  <ListItem key={participant.id} className={classes.participantItem}>
                    <ListItemAvatar>
                      <Avatar>
                        {participant.user?.name?.charAt(0) || "U"}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" style={{ fontWeight: 600 }}>
                            {participant.user?.name || "Atendente"}
                          </Typography>
                          {isAccepted && (
                            <Chip
                              icon={<CheckCircleIcon style={{ fontSize: 14 }} />}
                              label="Co-atendente Ativo"
                              size="small"
                              style={{ backgroundColor: "#e8f5e9", color: "#2e7d32" }}
                              className={classes.statusChip}
                            />
                          )}
                          {isPending && (
                            <Chip
                              icon={<ScheduleIcon style={{ fontSize: 14 }} />}
                              label="Convite Pendente"
                              size="small"
                              style={{ backgroundColor: "#fff8e1", color: "#f57f17" }}
                              className={classes.statusChip}
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        participant.invitedBy
                          ? `Convidado por ${participant.invitedBy.name}`
                          : "Participante"
                      }
                    />
                    <ListItemSecondaryAction>
                      {/* Titular ou Admin pode remover ou cancelar convite */}
                      {(isTicketOwner || currentUser.profile === "admin") && (
                        <Tooltip title={isPending ? "Cancelar convite" : "Remover do atendimento"}>
                          <IconButton
                            edge="end"
                            size="small"
                            onClick={() => handleRemove(participant.userId)}
                            style={{ color: "#d32f2f" }}
                          >
                            <DeleteOutlineIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })
            )}
          </List>
        )}
      </DialogContent>

      <DialogActions style={{ justifyContent: "space-between", padding: "12px 24px" }}>
        {isParticipant && !isTicketOwner ? (
          <Button
            variant="outlined"
            style={{ color: "#d32f2f", borderColor: "#d32f2f" }}
            startIcon={<ExitToAppIcon />}
            onClick={handleLeave}
          >
            Sair do Atendimento
          </Button>
        ) : (
          <div />
        )}
        <Button onClick={onClose} variant="outlined" color="default">
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TicketParticipantsModal;
